<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import type { DeviceNetwork } from '../model.js';
import { addressList } from '../lib/device-address.js';
import AppIcon from './AppIcon.vue';

/**
 * The device's primary address beside its OS facts: one click copies it. Other reported
 * addresses sit behind a quiet "+N" so the identity card stays uncluttered.
 */
const props = defineProps<{ network: DeviceNetwork }>();
const list = computed(() => addressList(props.network));
const open = ref(false);
const toast = ref('');
const root = ref<HTMLElement>();
let timer: ReturnType<typeof setTimeout> | undefined;

async function copy(address: string) {
  try {
    await navigator.clipboard.writeText(address);
    toast.value = `Copied ${address}`;
  } catch {
    toast.value = 'Copy is unavailable here. Select the address to copy it.';
  }
  clearTimeout(timer);
  timer = setTimeout(() => (toast.value = ''), 2200);
}
function onDocumentPointer(event: PointerEvent) {
  if (!(event.target instanceof Node) || !root.value?.contains(event.target)) open.value = false;
}
function onKey(event: KeyboardEvent) {
  if (event.key === 'Escape' && open.value) {
    event.preventDefault();
    open.value = false;
    root.value?.querySelector<HTMLElement>('.address-more')?.focus();
  }
}
watch(open, (value) => {
  if (value) document.addEventListener('pointerdown', onDocumentPointer, true);
  else document.removeEventListener('pointerdown', onDocumentPointer, true);
});
onBeforeUnmount(() => {
  clearTimeout(timer);
  document.removeEventListener('pointerdown', onDocumentPointer, true);
});
</script>

<template>
  <span v-if="list.primary" ref="root" class="device-address" @keydown="onKey">
    <span class="mono address-value">{{ list.primary.address }}</span>
    <button
      type="button"
      class="icon-button quiet address-copy"
      :aria-label="`Copy primary ${list.primary.family === 4 ? 'IPv4' : 'IPv6'} address`"
      :title="`Copy primary ${list.primary.family === 4 ? 'IPv4' : 'IPv6'}`"
      @click="copy(list.primary.address)"
    >
      <AppIcon :name="toast.startsWith('Copied') ? 'check' : 'clipboard'" />
    </button>
    <button
      v-if="list.others.length"
      type="button"
      class="address-more"
      :aria-expanded="open ? 'true' : 'false'"
      aria-haspopup="dialog"
      :title="`${list.others.length} more ${list.others.length === 1 ? 'address' : 'addresses'}`"
      @click="open = !open"
    >
      +{{ list.others.length }}
    </button>
    <div v-if="open" class="address-popover" role="dialog" aria-label="Reported addresses">
      <p class="address-popover-note">Reported when the device last connected.</p>
      <ul>
        <li v-for="item in list.all" :key="item.address">
          <span class="address-row-text">
            <span class="mono"
              >{{ item.address }}<span class="address-prefix">/{{ item.prefix }}</span></span
            >
            <span class="address-meta">
              {{ item.interface || 'Route source'
              }}<template v-if="item.primary"> · primary IPv{{ item.family }}</template
              ><template v-if="item.scope === 'private'"> · private</template>
            </span>
          </span>
          <button
            type="button"
            class="icon-button quiet small"
            :aria-label="`Copy ${item.address}`"
            :title="`Copy ${item.address}`"
            @click="copy(item.address)"
          >
            <AppIcon name="clipboard" />
          </button>
        </li>
      </ul>
    </div>
    <span class="address-toast" role="status" aria-live="polite" :data-visible="!!toast">{{
      toast
    }}</span>
  </span>
</template>

<style scoped>
.device-address {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: var(--text-muted);
  font-size: var(--text-sm);
}
.address-value {
  user-select: all;
}
.address-copy {
  width: 26px;
  min-height: 26px;
  margin-left: 2px;
  border-radius: var(--radius-xs);
  color: var(--text-faint);
}
.address-copy :deep(.ui-icon) {
  width: 14px;
  height: 14px;
}
.address-more {
  min-height: 22px;
  padding: 0 7px;
  border-radius: var(--radius-xs);
  background: var(--raised-2);
  border-color: transparent;
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: 560;
}
.address-popover {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 20;
  width: max-content;
  max-width: min(420px, calc(100vw - 32px));
  padding: 10px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-sm);
  background: var(--raised);
  box-shadow: var(--shadow-2);
}
.address-popover-note {
  padding: 2px 6px 8px;
  font-size: var(--text-xs);
  color: var(--text-faint);
}
.address-popover ul {
  list-style: none;
  margin: 0;
  padding: 0;
}
.address-popover li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 6px;
  border-radius: var(--radius-xs);
}
.address-popover li:hover {
  background: var(--raised-2);
}
.address-row-text {
  display: grid;
  gap: 1px;
  min-width: 0;
}
.address-row-text .mono {
  color: var(--text);
  overflow-wrap: anywhere;
}
.address-prefix,
.address-meta {
  color: var(--text-faint);
}
.address-meta {
  font-size: var(--text-xs);
}
.address-toast {
  position: fixed;
  left: 50%;
  bottom: calc(24px + var(--safe-bottom));
  z-index: 50;
  padding: 8px 14px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-sm);
  background: var(--raised-2);
  color: var(--text);
  font-size: var(--text-sm);
  box-shadow: var(--shadow-2);
  transform: translate(-50%, 6px);
  opacity: 0;
  pointer-events: none;
  transition:
    opacity var(--duration) var(--ease),
    transform var(--duration) var(--ease);
}
.address-toast[data-visible='true'] {
  opacity: 1;
  transform: translate(-50%, 0);
}
@media (prefers-reduced-motion: reduce) {
  .address-toast {
    transition: none;
  }
}
</style>
